import { createSelector } from "reselect";

import { classifyGaError } from "app/modules/ga-connect";
import type { GaErrorKind } from "app/modules/ga-connect";
import type { CompetitorsDataTypeMap } from "app/pages/benchmark-tracker/components/Report/CompetitorsModal/type";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import type { BenchmarkMetricName, MetricEntity } from "app/pages/benchmark-tracker/types/api.types";
import { ResponseError } from "app/services/fetchService/defaults";
import type { RootState } from "single-spa/store/types";

import { selectIsBenchmarkFetched } from "./entitySelectors";

const selectReport = (state: RootState) => state.benchmarkTracker.report;

export const selectHasReportError = createSelector(selectReport, (report) => !!report.error);

export const selectGaErrorKind = createSelector(selectReport, (report): GaErrorKind | null => {
    if (!report.error) {
        return null;
    }
    return report.error instanceof ResponseError
        ? classifyGaError(report.error.status, report.error.problem?.type)
        : "unknown";
});

export const selectAllMetrics = createSelector(selectReport, (report) => report.metrics);
const selectIsMetricsLoaded = createSelector(selectReport, (report) => report.isLoaded);

export const selectMetricByName = <R = MetricEntity>(metricName: BenchmarkMetricName) =>
    createSelector(selectAllMetrics, (metricsList) => metricsList && (metricsList[metricName] as R));

const selectMetadataState = createSelector(selectReport, (report) => report.metaData);

export const selectIsDigestSubscribed = createSelector(
    selectMetadataState,
    (metaData) => metaData.digestSubscriptionInfo.isSubscribed,
);

export const selectDigestGranularity = createSelector(
    selectMetadataState,
    (metaData) => metaData.digestSubscriptionInfo.granularity ?? BenchmarkGranularity.WEEKLY,
);

const selectCompetitorsModal = createSelector(selectReport, (report) => report.competitorsModal);

export const selectCompetitorsModalIsLoading = createSelector(selectCompetitorsModal, (modal) => modal.isLoading);

export const selectCompetitorByName = <T extends keyof CompetitorsDataTypeMap>(name: T) =>
    createSelector(selectCompetitorsModal, (modal) => modal.competitors?.[name] as CompetitorsDataTypeMap[T]);

export const selectIsReportLoaded = createSelector(
    selectIsBenchmarkFetched,
    selectIsMetricsLoaded,
    (isBenchmarksFetched, isMetricsLoaded) => isBenchmarksFetched && isMetricsLoaded,
);

const selectAssets = createSelector(selectReport, (report) => report.assets);
export const selectAssetsImages = createSelector(selectAssets, (assets) => assets.images);
export const selectIsAssetsImagesFetched = createSelector(selectAssets, (assets) => assets.isImagesFetched);

export const selectAudienceReachAutoDuration = (state: RootState) => selectReport(state).audienceReachAutoDuration;

const selectContextMenu = createSelector(selectReport, (report) => report.contextMenu);
export const selectPdfIsLoading = createSelector(selectContextMenu, (menu) => menu.isLoading);

const selectSubscriptionModal = createSelector(selectReport, (report) => report.subscriptionModal);
export const selectIsSubscriptionModalOpen = createSelector(selectSubscriptionModal, (modal) => modal.isOpen);
export const selectIsSubscriptionModalLoading = createSelector(selectSubscriptionModal, (modal) => modal.isLoading);
